import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@1trading/react';
import { Eye } from 'lucide-react';
import { type FieldValueRow, FieldValueTable } from '@/components/field-value-table';
import type { WhitelistedPayoutAddress } from '@/services/api/whitelisted-payout-addresses';
import { formatDate } from '@/utils/format';

interface DetailRow {
  label: string;
  value: string | undefined;
}

// UUID/Name/Type/Currency always render; the rest only when present (legacy parity —
// after the stale-identifier fix, at most one of IBAN/Wallet Address shows).
const buildDetailRows = (address: WhitelistedPayoutAddress): DetailRow[] =>
  [
    { label: 'UUID', value: address.uuid },
    { label: 'Name', value: address.name },
    { label: 'Type', value: address.type },
    { label: 'Currency', value: address.currency },
    { label: 'IBAN', value: address.iban },
    { label: 'Wallet Address', value: address.wallet_address },
    { label: 'Created At', value: address.created_at ? formatDate(address.created_at) : undefined },
    { label: 'Created By', value: address.created_by },
    { label: 'Updated At', value: address.updated_at ? formatDate(address.updated_at) : undefined },
    { label: 'Updated By', value: address.updated_by },
  ].filter((row) => !!row.value);

const toFieldValueRows = (rows: DetailRow[]): FieldValueRow[] => rows.map((row) => ({ key: row.label, label: row.label, value: row.value }));

interface AddressDetailDialogProps {
  address: WhitelistedPayoutAddress;
  onClose: () => void;
  /** Closes this dialog and opens the pre-filled edit form */
  onEdit: () => void;
  /** Closes this dialog and opens the delete confirmation */
  onDelete: () => void;
}

export const AddressDetailDialog = ({ address, onClose, onEdit, onDelete }: AddressDetailDialogProps) => (
  <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
    <DialogContent className="sm:max-w-[800px]">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Eye className="size-5" />
          Whitelisted Payout Address Details
        </DialogTitle>
      </DialogHeader>
      <div className="overflow-hidden rounded-md border">
        <FieldValueTable rows={toFieldValueRows(buildDetailRows(address))} />
      </div>
      <DialogFooter>
        <Button onClick={onDelete} type="button" variant="destructive">
          Delete
        </Button>
        <Button onClick={onEdit} type="button" variant="primary">
          Edit
        </Button>
        <Button onClick={onClose} type="button" variant="outline">
          Close
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
AddressDetailDialog.displayName = 'AddressDetailDialog';
